import { apiClient } from "@/lib/api/client";
import type {
  OtaCampaignDto,
  OtaCampaignPageDto,
  OtaHistoryEntryDto,
  OtaHistoryPageDto,
  OtaScheduleRequestDto,
  OtaStatusDto,
  OtaTargetDeviceDto,
  OtaUpdateRequestDto,
} from "@/lib/api/types/ota";

export async function listOtaTargetDevices(): Promise<OtaTargetDeviceDto[]> {
  const { data } = await apiClient.get<OtaTargetDeviceDto[]>("/ota/target-devices");
  return data;
}

export async function listOtaCampaigns(params: {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
} = {}): Promise<OtaCampaignPageDto> {
  const { data } = await apiClient.get<OtaCampaignPageDto>("/ota/campaigns", { params });
  return data;
}

export async function getOtaCampaign(campaignId: string): Promise<OtaCampaignDto> {
  const { data } = await apiClient.get<OtaCampaignDto>(`/ota/campaigns/${campaignId}`);
  return data;
}

export async function updateOtaCampaign(
  campaignId: string,
  body: { description?: string | null; status?: string | null },
): Promise<OtaCampaignDto> {
  const { data } = await apiClient.patch<OtaCampaignDto>(`/ota/campaigns/${campaignId}`, body);
  return data;
}

export async function deleteOtaCampaign(campaignId: string): Promise<void> {
  await apiClient.delete(`/ota/campaigns/${campaignId}`);
}

export async function retryFailedOtaJobs(campaignId: string): Promise<OtaCampaignDto> {
  const { data } = await apiClient.post<OtaCampaignDto>(`/ota/campaigns/${campaignId}/retry-failed`, {});
  return data;
}

export async function listOtaCampaignJobs(
  campaignId: string,
  params: { limit?: number; offset?: number; status?: string } = {},
): Promise<OtaHistoryPageDto> {
  const { data } = await apiClient.get<OtaHistoryPageDto>(`/ota/campaigns/${campaignId}/jobs`, { params });
  return data;
}

export async function createOtaCampaign(input: {
  file: File;
  version: string;
  description?: string;
  deviceIds?: string[];
  adminUserIds?: string[];
}): Promise<OtaCampaignDto> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("version", input.version);
  if (input.description?.trim()) {
    form.append("description", input.description.trim());
  }
  for (const deviceId of input.deviceIds ?? []) {
    form.append("device_ids", deviceId);
  }
  for (const adminUserId of input.adminUserIds ?? []) {
    form.append("admin_user_ids", adminUserId);
  }
  const { data } = await apiClient.post<OtaCampaignDto>("/ota/campaigns", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function getOtaHistory(deviceId: string): Promise<OtaHistoryEntryDto[]> {
  const { data } = await apiClient.get<OtaHistoryPageDto>(`/ota/devices/${deviceId}/history`);
  return data.items;
}

export async function getOtaStatus(deviceId: string): Promise<OtaStatusDto> {
  const history = await getOtaHistory(deviceId);
  return {
    current_firmware_version: history[0]?.current_version ?? null,
    last_job: history[0] ?? null,
  };
}

export async function postOtaUpdate(_deviceId: string, _body: OtaUpdateRequestDto): Promise<OtaHistoryEntryDto> {
  throw new Error("Use the dedicated OTA module to upload firmware and create rollouts.");
}

export async function postOtaSchedule(_deviceId: string, _body: OtaScheduleRequestDto): Promise<OtaHistoryEntryDto> {
  throw new Error("Use the dedicated OTA module to upload firmware and create rollouts.");
}

export async function postOtaRollback(_deviceId: string): Promise<OtaHistoryEntryDto> {
  throw new Error("Rollback is not available in the dedicated OTA module yet.");
}
